/**
 * Rosalie — the player character. A round plush sow, piglet-sized.
 *
 * She is the one asset in this project that everything else is judged against,
 * so the reasoning is written down rather than left in the numbers.
 *
 * ## What the silhouette has to do
 *
 * Spec §0: *"identifiable as its species at 64 px, from silhouette alone."* Four
 * shapes do that work here, and none of them is optional:
 *
 *   1. **A ball.** Her body is nearly spherical and takes ~78 % of her height.
 *      Roundness is what makes her a toy rather than an animal (D-002).
 *   2. **A snout that protrudes.** Not a muzzle blended into the head — a
 *      separate cylinder with a flat disc face, sticking out where a nose would
 *      be. It is also the game's only interaction verb (spec §5.2) and the
 *      thing that will point at what she is perceiving (D-002 amendment), so it
 *      has to be legible as its own limb.
 *   3. **Floppy forward ears.** Two rounded triangles hanging forward and out.
 *      They break the round head with a hard shape, which is what stops her
 *      reading as a generic ball at small sizes.
 *   4. **A curled tail.** Cheap, tiny, and it settles "pig" instantly.
 *
 * Stubby legs carry the fifth reading — that she is a *toy* pig — because the
 * proportion of body to limb is the whole difference between plush and animal.
 *
 * ## What is deliberately NOT modelled
 *
 * Felt seams, stitching, matted pile and wear are **shader and rig** work, never
 * geometry (D-003). There are no modelled ridges down her spine and there will
 * not be. She therefore looks bald and undetailed in M0a, and that is correct —
 * M0a judges motion and silhouette, M0b puts the fabric on her.
 *
 * Nothing that functions as a mouth, a brow or a blink exists, and nothing that
 * could grow into one is left here as a hook (D-002). The button eyes are welded
 * to the head on purpose: they are the one part of her that must never move.
 *
 * ## Scale
 *
 * `ROSALIE_SCALE` (0.55 × 0.34 × 0.32 m) is locked by D-012 and is the authority
 * for camera framing, cover, step heights and puzzle scale. Every number below
 * is derived from it. **Do not scale her up because a shot is awkward** — she is
 * small so that the farm is hostile and so that Otto cannot follow her.
 *
 * ## The rig seam
 *
 * The group is a named pivot hierarchy, not a skinned mesh — see `RosalieParts`
 * and `findRosalieParts()` at the bottom of this file. Animation transforms
 * these nodes; nothing in `src/anim/` needs to know how she is built.
 */

import * as THREE from 'three';
import { ROSALIE } from '@/render/palette';
import { ROSALIE_SCALE } from '@/world/testroom';
import { surface } from './materials';
import type { AssetDefinition } from './types';

/**
 * The four colours a plush piglet is painted in.
 *
 * Exists so a **littermate is literally this mesh** rather than a second model of
 * the same thing. They are three days old and identical, and that is exactly why
 * an empty patch of straw where one of them was reads as loss instead of as set
 * dressing — a sibling that looked merely similar would weaken the one image beat
 * 1 is built on.
 *
 * It is colour and nothing else. Do not add a proportion or a scale here: at that
 * point this stops being "the same toy in a different dye" and becomes a
 * parameterised character generator, which is how a cast starts looking
 * interchangeable. `SOW` is a separate model for exactly that reason.
 */
export interface PlushColours {
  readonly fabric: number;
  readonly fabricShadow: number;
  readonly seam: number;
  readonly button: number;
}

// ── Proportions, in metres ────────────────────────────────────────────────────
//
// Read these as fractions of her 0.32 m height. The ratios are the design; the
// absolute numbers are just D-012 applied to them.

/** Hip line. Legs pivot here, and the body sits low enough to overlap them. */
const HIP_Y = 0.105;

/**
 * Body ellipsoid: a ball, barely longer than it is wide.
 *
 * The first pass was 10 % longer and read as livestock rather than as a toy.
 * Length is the axis that decides that: shorten it and the body becomes a
 * sphere with a pig attached, which is the entire brief.
 */
const BODY = { x: 0.14, y: 0.124, z: 0.176, cy: 0.181, cz: -0.042 };


/**
 * Head. Smaller than half the body so she reads as body-first — a big head is
 * the single fastest way to make her cute rather than plush (D-002).
 *
 * Set LOW and tucked into the body, which buys two things: she reads rounder,
 * and the ears clear her crown without pushing past the locked 0.32 m height.
 */
const HEAD = { r: 0.103, cy: 0.192, cz: 0.156 };

/** Neck pivot, inside the body. The head swings from here, so the snout arcs. */
const NECK = { y: 0.183, z: 0.095 };

/** Snout: base radius, tip radius, and how far it protrudes past the head. */
const SNOUT = { rBase: 0.056, rTip: 0.041, length: 0.058, pitch: 0.16 };

/**
 * Ears. Sized against the head, not against her overall height: a pig ear is
 * roughly as long as the skull is deep, and anything smaller disappears at
 * 64 px, which is exactly what happened on the first pass.
 */
const EAR = { radius: 0.05, length: 0.104, flop: 0.72, splay: 0.38 };

/**
 * Legs. Slimmer and set further back than the first pass, both from G1:
 * *"the front legs look attached to the throat"* and *"the legs could be a bit
 * slimmer and a bit smaller in comparison to body."* The front pair moving back
 * is the bigger of the two fixes — a foreleg under the jaw makes her read as a
 * table, and it is why the shoulders looked wrong rather than the legs.
 */
const LEG = {
  radius: 0.029,
  hoofRadius: 0.034,
  /** Front pair narrower than the back — her chest is narrower than her rump, and
   *  forelegs set to the full rump width hang off the silhouette edge instead of
   *  standing under her. */
  frontSpreadX: 0.06,
  backSpreadX: 0.084,
  frontZ: 0.022,
  backZ: -0.152,
};

/**
 * Tail. G1: *"it looks like it has two branches which form nearly a ring at the
 * end. The tail should be straight with a slight curve."* The torus arc that
 * caused that is gone; this is a tapered tube with one gentle bend.
 */
const TAIL = { rBase: 0.0135, rTip: 0.0045, length: 0.105, bend: 0.055 };

/** Segment counts. Low enough to stay cheap, high enough that "round" survives. */
const SEG = { body: 26, bodyRings: 18, head: 22, headRings: 16, limb: 10 };

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Her torso: rump, barrel, shoulder and neck as ONE lofted surface.
 *
 * This replaces a body ellipsoid plus a separate neck ellipsoid, and the reason
 * is the G1 verdict: *"the head and body are not significantly separated… there
 * is a more straight and smooth transition between head, neck and body."*
 *
 * Two attempts failed before this one, and both failed the same way. Overlapping
 * two ellipsoids of different radii leaves a crease where the narrower one
 * emerges. Adding a third ellipsoid to bridge them just moved the problem: it
 * was wider than both neighbours at the join, so it read as a collar. A crease
 * between primitives cannot be fixed by adding primitives — the surface has to
 * be continuous, so the radius has to be a continuous function of length.
 *
 * `u` runs 0 at the rump to 1 at the neck opening, which is buried inside the
 * skull. The head stays a separate mesh because it has to rotate (D-002: head
 * and snout express attention), and it is wider than the neck where they meet,
 * which is how a real skull sits on a real neck.
 */
function buildTorsoGeometry(): THREE.BufferGeometry {
  const RINGS = 24;
  const SEGS = 26;
  const zBack = BODY.cz - BODY.z;
  const zFront = BODY.cz + BODY.z * 0.76;

  /** Rounds the rump off, so the loft closes into a ball rather than a tube. */
  const capped = (u: number): number => {
    const uc = 0.24;
    if (u >= uc) return 1;
    const k = 1 - u / uc;
    return Math.sqrt(Math.max(0, 1 - k * k));
  };

  // Width falls away faster than depth toward the neck: a pig's neck is narrow
  // side to side but stays deep top to bottom, and getting that wrong is what
  // makes a four-legged animal read as a sausage.
  //
  // The taper starts EARLY (0.26, not 0.4) and is strong. That is a head-on
  // fix: from the front you see her widest cross-section flanking her face, so
  // a barrel still at full width behind the shoulders reads as fat no matter
  // how good the profile looks. Slimming the shoulder while leaving the rump
  // full is what a pig actually is, and it costs nothing in side view.
  const halfWidth = (u: number): number => BODY.x * capped(u) * (1 - 0.62 * smoothstep(0.26, 1, u));
  const halfHeight = (u: number): number => BODY.y * capped(u) * (1 - 0.4 * smoothstep(0.4, 1, u));
  // The spine rises slightly toward the shoulders, so the neck leaves the body
  // pointing at the skull instead of at her chest.
  const centreY = (u: number): number => BODY.cy + 0.014 * smoothstep(0.3, 1, u);

  const positions: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i <= RINGS; i++) {
    const u = i / RINGS;
    const hw = halfWidth(u);
    const hh = halfHeight(u);
    const cy = centreY(u);
    const z = zBack + (zFront - zBack) * u;
    for (let j = 0; j <= SEGS; j++) {
      const theta = (j / SEGS) * Math.PI * 2;
      const c = Math.cos(theta);
      // Belly flattens where a stuffed toy settles under its own weight.
      const squat = c < 0 ? 1 + 0.1 * c : 1;
      positions.push(hw * Math.sin(theta), cy + hh * c * squat, z);
    }
  }

  for (let i = 0; i < RINGS; i++) {
    for (let j = 0; j < SEGS; j++) {
      const a = i * (SEGS + 1) + j;
      const b = a + SEGS + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }

  // Cap the neck opening. It is inside the skull at rest, but the head rotates,
  // and an open tube would show a hole the moment she looks far enough aside.
  const centreIndex = positions.length / 3;
  positions.push(0, centreY(1), zFront);
  const lastRing = RINGS * (SEGS + 1);
  for (let j = 0; j < SEGS; j++) {
    indices.push(centreIndex, lastRing + j + 1, lastRing + j);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Deform a unit sphere into her head: wedge-shaped toward the snout, so the
 * snout looks grown rather than glued on.
 */
function shapeHead(g: THREE.BufferGeometry): void {
  const pos = g.attributes['position'];
  if (!pos) return;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const taper = 1 - 0.36 * Math.max(0, z) ** 1.2;
    // Slight crown flattening — a domed skull reads as a teddy bear, not a pig.
    const crown = y > 0 ? 1 - 0.08 * y : 1;
    pos.setXYZ(i, x * taper * HEAD.r, y * crown * taper * HEAD.r * 0.96, z * HEAD.r * 1.12);
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
}

/**
 * Her tail: a tapered tube, mostly straight, bending gently along its length.
 *
 * Built along +Y and bent in Z, so the caller places it with one rotation. The
 * bend is quadratic in the length parameter, which keeps the base straight
 * where it leaves her body and puts all the curvature out near the tip — a
 * constant-curvature arc is what produced the "nearly a ring" read that G1
 * rejected.
 */
function buildTailGeometry(): THREE.BufferGeometry {
  const geo = new THREE.CylinderGeometry(TAIL.rTip, TAIL.rBase, TAIL.length, 8, 10);
  geo.translate(0, TAIL.length / 2, 0);
  const pos = geo.attributes['position'];
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      const t = Math.min(1, Math.max(0, y / TAIL.length));
      pos.setZ(i, pos.getZ(i) + TAIL.bend * t * t);
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  }
  return geo;
}

function mesh(geo: THREE.BufferGeometry, mat: THREE.Material, name: string): THREE.Mesh {
  const m = new THREE.Mesh(geo, mat);
  m.name = name;
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/** A pivot node. Empty, named, and the only thing animation is allowed to touch. */
function pivot(name: string, x: number, y: number, z: number): THREE.Object3D {
  const o = new THREE.Object3D();
  o.name = name;
  o.position.set(x, y, z);
  return o;
}

/**
 * One ear: a rounded triangle, thin, hanging forward and outward.
 *
 * Built from a cone rather than a plate so the base is thick and the tip is
 * soft — a flat card would catch the light like paper, and she is fabric.
 */
function buildEar(side: 1 | -1, fabric: THREE.Material): THREE.Object3D {
  // Pivot on the upper skull, so a flick rotates about the base the way a real
  // ear does. Coordinates are relative to the NECK pivot, not to the head mesh.
  const root = pivot(side > 0 ? 'ear.L' : 'ear.R', side * 0.045, 0.058, 0.045);

  // A rounded teardrop, not a cone. A cone comes to a point and reads as a horn
  // — which is exactly what the second pass looked like. An ear is broad and
  // soft at the base, narrower and still soft at the tip, and it cups forward.
  const geo = new THREE.SphereGeometry(1, 12, 12);
  const pos = geo.attributes['position'];
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      const taper = 1 - 0.5 * Math.max(0, y); // narrows toward the tip only
      pos.setXYZ(
        i,
        pos.getX(i) * EAR.radius * taper,
        (y * 0.5 + 0.5) * EAR.length, // base sits AT the pivot, tip away from it
        pos.getZ(i) * EAR.radius * 0.4 * taper + 0.22 * EAR.radius * y * y, // cupped
      );
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  }
  const ear = mesh(geo, fabric, 'ear');

  // Up first, then flopped forward: the tip has to clear the crown of her head
  // or the silhouette is a ball with a snout. The splay keeps both ears in
  // frame from a three-quarter view instead of one hiding behind the other.
  root.rotation.set(EAR.flop, 0, side * -EAR.splay);
  root.add(ear);
  return root;
}

/** One leg: a stubby cylinder with a rounded trotter, pivoting at the hip. */
function buildLeg(name: string, x: number, z: number, fabric: THREE.Material): THREE.Object3D {
  const root = pivot(name, x, HIP_Y, z);

  // The shaft runs UP past the hip and into the body. Ending it at the pivot
  // left a visible gap between belly and leg from every angle, because the
  // ellipsoid's underside rises toward her ends and the hip sits below it.
  const top = 0.045;
  // Chosen so the squashed trotter's lowest vertex lands exactly on y = 0. The
  // asset contract is "sits on the grid without floating or sinking", and the
  // bounds test in asset.spec.ts holds this to a millimetre.
  const bottom = -HIP_Y + LEG.hoofRadius * 0.66;
  const length = top - bottom;
  // Flares slightly downward so the trotter grows out of the leg instead of
  // being a wider ball stuck on the end of it.
  const shaft = new THREE.CylinderGeometry(LEG.radius * 0.86, LEG.hoofRadius, length, SEG.limb, 1);
  shaft.translate(0, bottom + length / 2, 0);
  root.add(mesh(shaft, fabric, 'leg-shaft'));

  // The trotter is a squashed ball, not a hoof. She is a toy; toys have paws.
  const hoof = new THREE.SphereGeometry(LEG.hoofRadius, SEG.limb, 8);
  hoof.scale(1, 0.66, 1);
  hoof.translate(0, bottom, 0);
  root.add(mesh(hoof, fabric, 'trotter'));

  return root;
}

/**
 * Build a plush piglet.
 *
 * `colours` defaults to Rosalie's, so `buildPlush()` is her and nothing about the
 * player character changed when this became parameterised. See `PlushColours`.
 * The group is named for the dye, not for her, or two littermates in one scene
 * would both answer to `getObjectByName('rosalie')`.
 */
export function buildPlush(colours: PlushColours = ROSALIE, name = 'rosalie'): THREE.Group {
  const group = new THREE.Group();
  group.name = name;

  const fabric = surface('fabric', colours.fabric);
  const skin = surface('fabric', colours.fabricShadow);
  const button = surface('fabric', colours.button);

  // ── squash root ────────────────────────────────────────────────────────────
  // Everything above the hips hangs off this node so a landing can compress her
  // toward the floor without dragging her feet with it. D-002 caps that at
  // 3–5 %: enough to read as stuffing, far short of comic.
  const squash = pivot('squash', 0, 0, 0);
  group.add(squash);

  // ── body ───────────────────────────────────────────────────────────────────
  // Its own material, and the only one in the game with a seam.
  //
  // Spec §2 asks for "a visible felt seam down her spine". D-003 puts that in
  // the shader rather than the geometry, and the shader draws it along the
  // mesh's local X = 0 — which works here and nowhere else, because the torso is
  // ONE lofted surface (the G1 rebuild) whose local midline IS her spine. Give
  // this material to a leg and you get a stripe down the leg.
  squash.add(
    mesh(
      buildTorsoGeometry(),
      surface('fabric', colours.fabric, { seam: true, seamColour: colours.seam }),
      'body',
    ),
  );

  // ── head, on a neck pivot ──────────────────────────────────────────────────
  const head = pivot('head', 0, NECK.y, NECK.z);
  squash.add(head);

  const headGeo = new THREE.SphereGeometry(1, SEG.head, SEG.headRings);
  shapeHead(headGeo);
  headGeo.translate(0, HEAD.cy - NECK.y, HEAD.cz - NECK.z);
  head.add(mesh(headGeo, fabric, 'head-mesh'));

  // Button eyes. Welded to the head and never animated — that is the mechanism
  // by which the player projects an interior life onto her (D-002). Matte, so
  // they catch shape rather than shine.
  for (const side of [1, -1] as const) {
    // Forward on the skull, not on its sides. Set wide, they sit where the ears
    // belong and she reads as a hippo; set forward, the face resolves.
    // G1: *"the eyes are very close to the nose and should be moved up a bit."*
    // Raised and pulled back off the snout. On a real pig the eye sits well up
    // the skull, roughly level with the ear root, not beside the nostrils.
    const az = side * 0.5; // outward from centre
    const el = 0.42; // well up the skull, clear of the snout
    const r = HEAD.r * 0.9;
    // Deliberately large for her head. A real button is oversized on a small
    // plush, and it has to survive being the only feature on an otherwise blank
    // face — she has no brow, no mouth and no blink to carry a read (D-002).
    // A flattened dome, not a disc. A disc goes edge-on from three-quarter
    // angles and reads as a dark slit cut into her face, which is the one thing
    // a button eye must never do.
    const eye = new THREE.SphereGeometry(0.019, 12, 8);
    eye.scale(1, 1, 0.55);
    const m = mesh(eye, button, side > 0 ? 'eye.L' : 'eye.R');
    m.position.set(
      Math.sin(az) * Math.cos(el) * r * 0.94,
      HEAD.cy - NECK.y + Math.sin(el) * r * 0.88,
      HEAD.cz - NECK.z + Math.cos(az) * Math.cos(el) * r * 1.02,
    );
    m.lookAt(m.position.clone().add(new THREE.Vector3(Math.sin(az) * 0.6, 0.2, 1)));
    head.add(m);
  }

  head.add(buildEar(1, fabric), buildEar(-1, fabric));

  // ── snout, on its own pivot ────────────────────────────────────────────────
  // Separate from the head because it is the interaction verb and the
  // perception indicator: it samples the air, it points at what she can act on
  // (D-002 amendment, spec §5.2/§9). Attention, never emotion.
  const snout = pivot('snout', 0, HEAD.cy - NECK.y - 0.012, HEAD.cz - NECK.z + HEAD.r * 0.62);
  snout.rotation.x = -SNOUT.pitch; // tipped up, the way a pig carries it
  head.add(snout);

  const barrel = new THREE.CylinderGeometry(SNOUT.rTip, SNOUT.rBase, SNOUT.length, 16, 1);
  barrel.rotateX(Math.PI / 2); // cylinder axis onto +Z, the asset forward axis
  barrel.translate(0, 0, SNOUT.length / 2);
  snout.add(mesh(barrel, skin, 'snout'));

  // The disc face. Flat enough to read as a snout in silhouette, but with a
  // rounded rim — a hard-edged cylinder cap gave her a boxy rectangular profile
  // at 64 px, which is the one size where the snout has to carry the species.
  const disc = new THREE.SphereGeometry(SNOUT.rTip, 16, 10);
  disc.scale(1, 1, 0.34);
  disc.translate(0, 0, SNOUT.length);
  snout.add(mesh(disc, skin, 'snout-disc'));

  // Nostrils. G1: *"the nostrils are not visible."* They were 8 mm discs sunk
  // flush into a disc that is usually seen at an angle, so they had no edge to
  // catch light. Now larger, wider apart, and standing slightly PROUD of the
  // face so they read as holes by their own shading rather than by colour alone.
  for (const side of [1, -1] as const) {
    const nostril = new THREE.SphereGeometry(0.011, 10, 8);
    nostril.scale(0.75, 1.2, 0.6);
    // z is the whole fix. At +0.006 they sat INSIDE the snout disc — the disc is
    // a sphere cap 14 mm deep, so a nostril placed just past the disc's centre
    // is still buried in it. They now stand ~5 mm proud of the face at the x
    // offset where they actually sit, which is what makes them read at all.
    nostril.translate(side * 0.019, -0.004, SNOUT.length + 0.011);
    snout.add(mesh(nostril, button, side > 0 ? 'nostril.L' : 'nostril.R'));
  }

  // ── tail ───────────────────────────────────────────────────────────────────
  // One curl. It costs 300 triangles and it is worth every one of them: it is
  // the cheapest "pig" signal in the whole silhouette.
  // Base set INSIDE the body rather than on its surface, so it grows out of her
  // instead of being stuck on — the other half of the G1 tail note.
  const tail = pivot('tail', 0, BODY.cy + 0.052, BODY.cz - BODY.z * 0.86);
  squash.add(tail);
  tail.add(mesh(buildTailGeometry(), fabric, 'tail'));
  // Up and back, the way a relaxed pig carries it.
  tail.rotation.set(-0.62, 0, 0);

  // ── legs ───────────────────────────────────────────────────────────────────
  // Outside the squash node on purpose: her feet stay on the floor when her
  // body compresses. Front pair set slightly narrower than the back, which is
  // how four-legged animals actually stand and reads as intent at a glance.
  group.add(
    buildLeg('leg.FL', LEG.frontSpreadX, LEG.frontZ, fabric),
    buildLeg('leg.FR', -LEG.frontSpreadX, LEG.frontZ, fabric),
    buildLeg('leg.BL', LEG.backSpreadX, LEG.backZ, fabric),
    buildLeg('leg.BR', -LEG.backSpreadX, LEG.backZ, fabric),
  );

  return group;
}

/**
 * The animatable nodes, resolved by name.
 *
 * `src/anim/` drives these and nothing else. Keeping the lookup here means the
 * mesh can be rebuilt — reproportioned, re-segmented, replaced outright — without
 * a single clip changing, as long as the names survive.
 */
export interface RosalieParts {
  /** Whole-body compression. Non-uniform scale only, and capped at 3–5 % (D-002). */
  squash: THREE.Object3D;
  /** Neck pivot. Driven by the perception target, never by a mood value. */
  head: THREE.Object3D;
  /** Points at what she is attending to or acting on. Same rule as the head. */
  snout: THREE.Object3D;
  earL: THREE.Object3D;
  earR: THREE.Object3D;
  tail: THREE.Object3D;
  legFL: THREE.Object3D;
  legFR: THREE.Object3D;
  legBL: THREE.Object3D;
  legBR: THREE.Object3D;
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`rosalie: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

/** Resolve the rig nodes of a group produced by `rosalieAsset.create()`. */
export function findRosalieParts(root: THREE.Object3D): RosalieParts {
  return {
    squash: required(root, 'squash'),
    head: required(root, 'head'),
    snout: required(root, 'snout'),
    earL: required(root, 'ear.L'),
    earR: required(root, 'ear.R'),
    tail: required(root, 'tail'),
    legFL: required(root, 'leg.FL'),
    legFR: required(root, 'leg.FR'),
    legBL: required(root, 'leg.BL'),
    legBR: required(root, 'leg.BR'),
  };
}

export const rosalieAsset: AssetDefinition = {
  id: 'rosalie',
  label: 'Rosalie',
  description:
    'The player. A piglet-sized plush sow: spherical body, stubby legs, protruding snout disc, floppy forward ears, curled tail. Seams and pile are shader work (D-003).',
  footprint: {
    width: ROSALIE_SCALE.width,
    height: ROSALIE_SCALE.height,
    length: ROSALIE_SCALE.length,
  },
  create: () => buildPlush(),
};
